import React, { useState, useMemo } from 'react';
import { Box, Text, HeaderPage, ButtonIcon } from '@backstage/ui';
import { Entity, RELATION_PART_OF } from '@backstage/catalog-model';
import { Content, WarningPanel } from '@backstage/core-components';
import {
  attachComponentData,
  useElementFilter,
} from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import {
  AsyncEntityProvider,
  useRelatedEntities,
  useStarredEntity,
  UnregisterEntityDialog,
} from '@backstage/plugin-catalog-react';
import {
  useNavigate,
  useResolvedPath,
  Route as RouterRoute,
  Routes,
} from 'react-router-dom';
import { RiStarLine, RiStarFill } from '@remixicon/react';
import { appTranslationRef } from '../../../../core/i18n';
import { useEntityFromUrl, useEntityInspector } from '../../hooks';
import { EntityContextMenu } from './entity-context-menu.component';
import { EntityInspector } from '../../components/entity-inspector';
import elStyles from './entity-layout.module.css';

// ============================================================================
// EntityLayout.Route — marker component (same pattern as plugin)
// ============================================================================

const DATA_KEY = 'plugin.catalog.entityLayoutRoute';

interface EntityLayoutRouteProps {
  path: string;
  title: string;
  children: React.ReactNode;
  if?: (entity: Entity) => boolean;
  tabProps?: Record<string, unknown>;
}

const Route = (_props: EntityLayoutRouteProps) => null;
attachComponentData(Route, DATA_KEY, true);
attachComponentData(Route, 'core.gatherMountPoints', true);

interface TabRoute {
  path: string;
  title: string;
  children: React.ReactNode;
  tabProps?: Record<string, unknown>;
}

// ============================================================================
// EntityLayout — main wrapper
// ============================================================================

interface EntityLayoutProps {
  children: React.ReactNode;
  NotFoundComponent?: React.ReactNode;
}

const EntityLayoutInner = ({
  children,
  NotFoundComponent,
}: EntityLayoutProps) => {
  const { entity, loading, error, refresh, kind, namespace, name } =
    useEntityFromUrl();
  const inspector = useEntityInspector();
  const { t } = useTranslationRef(appTranslationRef);
  const navigate = useNavigate();
  const basePath = useResolvedPath('.');

  const { toggleStarredEntity, isStarredEntity } = useStarredEntity(
    entity ?? `${kind}:${namespace}/${name}`,
  );

  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);

  // Extract routes from children using the same mechanism as the plugin
  const routes = useElementFilter(
    children,
    elements =>
      elements
        .selectByComponentData({
          key: DATA_KEY,
          withStrictError:
            'Child of EntityLayout must be an EntityLayout.Route',
        })
        .getElements()
        .flatMap(({ props: elementProps }): TabRoute[] => {
          if (!entity) return [];
          const ifFn = elementProps.if as
            | ((entity: Entity) => boolean)
            | undefined;
          if (ifFn && !ifFn(entity)) return [];
          return [
            {
              path: elementProps.path as string,
              title: elementProps.title as string,
              children: elementProps.children as React.ReactNode,
              tabProps: elementProps.tabProps as
                | Record<string, unknown>
                | undefined,
            },
          ];
        }),
    [entity],
  );

  // Derive entity metadata
  const entityName = entity?.metadata.title ?? entity?.metadata.name ?? name;

  // Fetch parent entities (RELATION_PART_OF → system/domain) for breadcrumbs
  const { entities: parentEntities } = useRelatedEntities(entity!, {
    type: RELATION_PART_OF,
  });

  const breadcrumbs = useMemo(() => {
    const crumbs: Array<{ label: string; href: string }> = [
      { label: t('catalog.pageTitle'), href: '/catalog' },
    ];
    if (parentEntities && parentEntities.length > 0) {
      const parent = parentEntities[0];
      const ns = parent.metadata.namespace ?? 'default';
      crumbs.push({
        label: parent.metadata.title ?? parent.metadata.name,
        href: `/catalog/${ns}/${parent.kind}/${parent.metadata.name}`,
      });
    }
    return crumbs;
  }, [parentEntities, t]);

  // Build tabs for HeaderPage
  const headerTabs = useMemo(() => {
    return routes.map(route => {
      const href =
        route.path === '/'
          ? basePath.pathname
          : `${basePath.pathname}${route.path.startsWith('/') ? '' : '/'}${
              route.path
            }`;
      return {
        id: route.path,
        label: route.title,
        href,
        matchStrategy:
          route.path === '/' ? ('exact' as const) : ('prefix' as const),
      };
    });
  }, [routes, basePath.pathname]);

  // Custom actions: favorite + context menu
  const customActions = entity ? (
    <>
      <ButtonIcon
        size="small"
        variant="tertiary"
        icon={
          isStarredEntity ? <RiStarFill size={18} /> : <RiStarLine size={18} />
        }
        onPress={toggleStarredEntity}
        aria-label={t('entityLayout.favorite')}
      />
      <EntityContextMenu
        onInspect={inspector.open}
        onUnregister={() => setConfirmationDialogOpen(true)}
      />
    </>
  ) : undefined;

  return (
    <AsyncEntityProvider
      entity={entity}
      loading={loading}
      error={error}
      refresh={refresh}
    >
      <Box className={elStyles.pageShell}>
        {/* Header + Tabs */}
        <HeaderPage
          title={entityName}
          breadcrumbs={breadcrumbs}
          tabs={entity && routes.length > 0 ? headerTabs : undefined}
          customActions={customActions}
        />

        {/* Loading indicator */}
        {loading && !entity && (
          <Box className={elStyles.loadingBar} />
        )}

        {/* Content */}
        {entity && routes.length > 0 && (
          <Content stretch>
            <Routes>
              {routes.map((route: TabRoute) => (
                <RouterRoute
                  key={route.path}
                  path={route.path === '/' ? '/' : `${route.path}/*`}
                  element={route.children}
                />
              ))}
            </Routes>
          </Content>
        )}

        {/* Error state */}
        {error && (
          <Content>
            <WarningPanel severity="error" title={error.toString()} />
          </Content>
        )}

        {/* Not found state */}
        {!loading && !error && !entity && (
          <Content>
            {NotFoundComponent ?? (
              <WarningPanel title={t('entityLayout.notFound')}>
                <Text variant="body-small">
                  There is no {kind} with the requested kind, namespace, and name.
                </Text>
              </WarningPanel>
            )}
          </Content>
        )}

        {/* Entity Inspector dialog */}
        {entity && inspector.isOpen && (
          <EntityInspector entity={entity} />
        )}

        {/* Unregister dialog */}
        {entity && (
          <UnregisterEntityDialog
            open={confirmationDialogOpen}
            entity={entity}
            onConfirm={async () => {
              setConfirmationDialogOpen(false);
              navigate('/catalog');
            }}
            onClose={() => setConfirmationDialogOpen(false)}
          />
        )}
      </Box>
    </AsyncEntityProvider>
  );
};

// Attach Route as static property
export const EntityLayout = Object.assign(EntityLayoutInner, { Route });
